import fetcher from '@lib/fetcher'
import {
	getAllProductPathsQuery,
	getAllProductsQuery,
	getProductQuery,
	getProductRecommendationsQuery,
	getProductsByVariantIdQuery
} from '@lib/queries'
import { getProductByIdQuery, getProductListQuery } from '@lib/queries/get-product-query'
import type { PageInfo, ProductPath } from '@lib/types'
import {
	normalizeCollectionProduct,
	normalizeCollectionProductSamples,
	normalizeProduct,
	normalizeProductRecommendations
} from '@lib/utils/normalizers'

/**
 * Get all the prducts by slug.
 * @param slug the product slug
 * @returns
 */
export const getProductBySlug = async (slug: string, shouldOmitHiddenVariants = true) => {
	const { product } = await fetcher({ variables: { slug }, query: getProductQuery })

	if (product)
		return normalizeProduct(product, {
			shouldOmitHiddenVariants
		})

	return null
}

/**
 * Get all recommendations for a product.
 * @param id the product id
 * @returns all the recommendations for the product
 */
export const getProductRecommendationsById = async (id: string) => {
	const { productRecommendations } = await fetcher({
		query: getProductRecommendationsQuery,
		variables: { id }
	})

	if (productRecommendations) {
		return normalizeProductRecommendations(productRecommendations)
	}

	return []
}

/**
 * @returns all the products
 */
export const getAllProducts = async () => {
	const allProduct = await fetcher({ query: getAllProductsQuery })
	return allProduct
}

/**
 * Recursively get all product paths using Shopify's pagination
 *
 * @returns all the paths for the products
 */
export const getAllProductPaths = async (
	productPaths?: ProductPath[],
	after?: PageInfo['endCursor']
): Promise<ProductPath[]> => {
	const { products } = await fetcher({
		query: getAllProductPathsQuery,
		variables: {
			after: after ?? null
		}
	})

	const { pageInfo, nodes } = products

	const mergedProductsPaths = productPaths ? [...productPaths, ...nodes] : [...nodes]

	if (pageInfo.hasNextPage) {
		return getAllProductPaths(mergedProductsPaths, pageInfo.endCursor)
	}

	return mergedProductsPaths
}

/**
 * @returns products from an array of productVariants
 */
export const getProductVariantInfo = async (productVariants: string[]) => {
	const products = await fetcher({
		query: getProductsByVariantIdQuery,
		variables: {
			productVariants
		}
	})

	const { nodes } = products || {}

	return nodes.map(
		(productObj: {
			product: {
				productId: string
				description: string
			}
		}) => {
			if (!productObj) return null

			const { product, ...rest } = productObj || {}

			return {
				...product,
				...rest
			}
		}
	)
}

export const getShopifyProductById = async (productId: string) => {
	try {
		const response = await fetcher({
			variables: { id: productId },
			query: getProductByIdQuery
		})

		const { product } = response

		if (!product) {
			console.warn(`Product not found for ID: ${productId}`)
			return null
		}

		return normalizeCollectionProduct({ node: product })
	} catch (error) {
		console.error(`Error fetching product with ID ${productId}:`, error)
		return null
	}
}

export const getProductSamplesWithCollectionLikeFormat = async (
	productVariantIds: string[],
	customizationType?: 'NMC' | 'RMC'
) => {
	const { nodes } = await fetcher({
		variables: {
			variantIds: productVariantIds
		},
		query: getProductListQuery
	})
	const products =
		nodes && nodes.length
			? normalizeCollectionProductSamples(
					nodes.filter((n: any) => !!n).map((n: { product: unknown }) => ({ node: n.product })),
					customizationType
				)
			: []

	return products
}
